import React, { useState } from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, Sector } from 'recharts';

export interface PieChartData {
  title?: string;
  subtitle: string;
  inOr?: number;
  inSurgery?: number;
  closing?: number;
  procedureComplete?: number;
  cleaning?: number;
  orReady?: number;
}

interface PieChartProps {
  data: PieChartData;
}

const PieChart = (props: PieChartProps) => {
  const [selection, setSelection] = useState<number | undefined>();

  const onPieEnter = (_, index) => {
    setSelection(index);
  };
  const renderActiveShape = props => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      label,
      textColor,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text fontSize={12} fontWeight="bold" x={cx} y={cy} dy={110} textAnchor="middle" fill={textColor || fill}>
          {payload.label}
        </text>
        <text fontSize={12} x={cx} y={cy} dy={126} textAnchor="middle" fill={textColor || fill}>
          {Math.round(props.value)} min
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />{' '}
        {/*<path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />*/}{' '}
        {/*<circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />*/}{' '}
        {/*<text fontSize={12} x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#fff">*/}{' '}
        {/*  {label}*/} {/*</text>*/} {/*<text*/} {/*  opacity={0.5}*/} {/*  fontSize={12}*/}{' '}
        {/*  x={ex + (cos >= 0 ? 1 : -1) * 12}*/} {/*  y={ey}*/} {/*  dy={18}*/} {/*  textAnchor={textAnchor}*/}{' '}
        {/*  fill="#fff"*/} {/*>*/} {/*  {`(${(percent * 100).toFixed(2)}%)`}*/} {/*</text>*/}
      </g>
    );
  };

  const data = [
    props.data.inOr !== undefined
      ? {
          label: 'In OR',
          value: props.data.inOr,
          color: 'rgba(12,135,201,0.58)',
        }
      : undefined,
    props.data.inSurgery !== undefined
      ? {
          label: 'In Surgery',
          value: props.data.inSurgery,
          color: 'rgb(12,201,77)',
          boxShadow: '0 0 0 2px #fff, 0 0 0 16px #fff',
        }
      : undefined,
    props.data.closing !== undefined
      ? {
          label: 'Closing',
          value: props.data.closing,
          color: 'rgba(12,135,201,0.58)',
        }
      : undefined,
    props.data.procedureComplete !== undefined
      ? {
          label: 'Procedure Complete',
          value: props.data.procedureComplete,
          color: 'rgba(12,135,201,0.58)',
        }
      : undefined,
    props.data.cleaning !== undefined
      ? {
          label: 'Cleaning',
          value: props.data.cleaning,
          color: 'rgba(12,135,201,0.58)',
          textColor: 'rgba(0,149,231,0.76)',
        }
      : undefined,
    props.data.orReady !== undefined
      ? {
          label: 'OR Ready',
          value: props.data.orReady,
          color: 'rgba(12,135,201,0.58)',
          textColor: 'rgba(0,149,231,0.76)',
        }
      : undefined,
  ].filter(value => value !== undefined);

  return (
    <>
      <RechartsPieChart width={200} height={260}>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          activeIndex={selection}
          activeShape={selection !== undefined ? renderActiveShape : undefined}
          innerRadius={50}
          outerRadius={80}
          fill="#8884d8"
          stroke="none"
          dataKey="value"
          startAngle={90}
          endAngle={-270}
          paddingAngle={4}
          cornerRadius={2}
          onMouseEnter={onPieEnter}
          labelLine={false}
          label={props => {
            if (Math.abs(props.endAngle - props.startAngle) < 10) {
              return null;
            }

            const innerRadius = props.innerRadius;
            const outerRadius = props.outerRadius;
            const cx = props.cx;
            const cy = props.cy;
            const midAngle = props.midAngle;

            const RADIAN = Math.PI / 180;
            const radius = innerRadius + (outerRadius - innerRadius) * 0.5;

            return (
              <text
                x={cx + radius * Math.cos(-midAngle * RADIAN)}
                y={cy + radius * Math.sin(-midAngle * RADIAN)}
                fontSize={13}
                fontWeight={500}
                fill="white"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {`${(props.percent * 100).toFixed(0)}%`}
              </text>
            );
          }}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry!.color} />
          ))}
        </Pie>{' '}
        {props.data.title && (
          <text x="50%" y="50%" fontSize={18} fontWeight={500} fill="white" textAnchor="middle" dominantBaseline="auto">
            {props.data.title}
          </text>
        )}
        <text
          x="50%"
          y={props.data.title ? '53%' : 100}
          fontSize={13}
          fontWeight={500}
          fill="rgb(125	163	206)"
          textAnchor="middle"
          dominantBaseline={props.data.title ? 'hanging' : 'middle'}
        >
          {Math.round(
            // @ts-ignore
            (props.data.inSurgery +
              // @ts-ignore
              props.data.inOr +
              // @ts-ignore
              props.data.closing +
              // @ts-ignore
              props.data.procedureComplete +
              // @ts-ignore
              props.data.cleaning +
              // @ts-ignore
              props.data.orReady) as number
          )}{' '}
          min
        </text>
      </RechartsPieChart>
    </>
  );
};

export default PieChart;
